import { useEffect, useState } from "react";
import { ShieldAlert, Sparkles } from "lucide-react";
import api from "../../api";

// Structured risk assessment (backend clinical/safety/risk.py). Controlled: the page
// owns `value` and sends it as `risk_assessment` with the consultation save
// (or to POST /patients/<id>/risk on the record page). The phrase check
// points at wording worth a second look and can suggest a level; the
// clinician always decides.
const GROUPS = [
  { key: "suicidal_ideation", label: "Suicidal ideation", options: [["none", "None reported"], ["passive", "Passive"], ["active_no_plan", "Active, no plan"], ["active_plan_or_intent", "Active, with plan or intent"]] },
  { key: "self_harm", label: "Self-harm", options: [["none", "None"], ["historical", "Historical only"], ["recent", "Recent (last 3 months)"]] },
  { key: "harm_to_others", label: "Risk to others", options: [["none", "None"], ["some", "Some, not concerning"], ["concerning", "Concerning"]] },
];
const LEVELS = [["low", "Low"], ["moderate", "Moderate"], ["high", "High"]];
const LEVEL_STYLE = { low: "bg-emerald-600 border-emerald-600", moderate: "bg-amber-500 border-amber-500", high: "bg-rose-600 border-rose-600" };
const REQUIRED = ["suicidal_ideation", "self_harm", "harm_to_others", "overall_risk"];

export const emptyRisk = () => ({ suicidal_ideation: "", self_harm: "", harm_to_others: "", protective_factors: "", overall_risk: "", action_taken: "" });

// "empty" (nothing chosen), "incomplete", or "complete" - pages only send a complete one.
export function riskStatus(v) {
  if (!v) return "empty";
  const chosen = REQUIRED.filter((k) => v[k]);
  if (chosen.length === 0 && !v.protective_factors && !v.action_taken) return "empty";
  if (chosen.length < REQUIRED.length) return "incomplete";
  if ((v.overall_risk === "moderate" || v.overall_risk === "high") && !(v.action_taken || "").trim()) return "incomplete";
  return "complete";
}

export default function RiskAssessmentSection({ value, onChange, noteText = "", disabled = false }) {
  const v = value || emptyRisk();
  const [flags, setFlags] = useState(null);
  const set = (key, val) => onChange({ ...v, [key]: val });

  useEffect(() => {
    const text = (noteText || "").trim();
    if (text.length < 8) { setFlags(null); return undefined; }
    const timer = setTimeout(() => {
      api.riskFlags(text).then((r) => setFlags(r.data)).catch(() => setFlags(null));
    }, 800);
    return () => clearTimeout(timer);
  }, [noteText]);

  const needsAction = v.overall_risk === "moderate" || v.overall_risk === "high";
  const status = riskStatus(v);

  return (
    <div className="p-4 space-y-4 bg-rose-50/30 border border-rose-200/70 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-[11px] font-bold text-rose-800 uppercase tracking-wide">
          <ShieldAlert className="w-4 h-4" /> Risk assessment
        </h4>
        <span className={`text-[11px] font-semibold ${status === "complete" ? "text-emerald-700" : "text-rose-700"}`}>
          {status === "complete" ? "Complete" : status === "incomplete" ? "Incomplete" : "Not started"}
        </span>
      </div>

      {flags?.phrases?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1.5">
          <p className="flex items-center gap-1.5 font-semibold"><Sparkles className="w-3.5 h-3.5" /> Wording in your notes worth a second look</p>
          <div className="flex flex-wrap gap-1.5">
            {flags.phrases.map((p) => (
              <span key={p.text} className="rounded-full bg-white border border-amber-200 px-2 py-0.5">“{p.text}” · {p.category}</span>
            ))}
          </div>
          {flags.suggested_level && v.overall_risk !== flags.suggested_level && !disabled && (
            <p>
              Suggested overall risk: <strong className="capitalize">{flags.suggested_level}</strong>.{" "}
              <button type="button" onClick={() => set("overall_risk", flags.suggested_level)} className="underline font-semibold">Use this</button>{" "}
              <span className="text-amber-700">(you decide)</span>
            </p>
          )}
        </div>
      )}

      {GROUPS.map((g) => (
        <fieldset key={g.key} disabled={disabled}>
          <legend className="text-xs font-semibold text-gray-700 mb-1.5">{g.label}</legend>
          <div className="flex flex-wrap gap-2">
            {g.options.map(([val, label]) => (
              <label key={val} className={`relative normal-case font-sans tracking-normal cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${v[g.key] === val ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}>
                <input type="radio" className="sr-only" name={`risk_${g.key}`} value={val} checked={v[g.key] === val} onChange={() => set(g.key, val)} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
      ))}

      <div>
        <label htmlFor="risk_protective" className="text-xs font-semibold text-gray-700 mb-1 block">Protective factors</label>
        <textarea id="risk_protective" disabled={disabled} value={v.protective_factors || ""} onChange={(e) => set("protective_factors", e.target.value)}
          placeholder="People, plans, reasons for living" className="w-full rounded-lg border border-gray-300 p-2 text-sm bg-white min-h-[70px]" />
      </div>

      <fieldset disabled={disabled}>
        <legend className="text-xs font-semibold text-gray-700 mb-1.5">Overall risk</legend>
        <div className="flex flex-wrap gap-2">
          {LEVELS.map(([val, label]) => (
            <label key={val} className={`relative normal-case font-sans tracking-normal cursor-pointer rounded-lg border px-4 py-1.5 text-xs font-bold transition-colors ${v.overall_risk === val ? `${LEVEL_STYLE[val]} text-white` : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"}`}>
              <input type="radio" className="sr-only" name="risk_overall" value={val} checked={v.overall_risk === val} onChange={() => set("overall_risk", val)} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="risk_action" className="text-xs font-semibold text-gray-700 mb-1 block">
          Action taken {needsAction && <span className="text-rose-700">(required for {v.overall_risk} risk)</span>}
        </label>
        <textarea id="risk_action" disabled={disabled} value={v.action_taken || ""} onChange={(e) => set("action_taken", e.target.value)}
          placeholder={needsAction ? "e.g. Safety plan reviewed, family informed with consent, follow-up call booked for tomorrow" : "Optional for low risk"}
          className={`w-full rounded-lg border p-2 text-sm bg-white min-h-[70px] ${needsAction && !(v.action_taken || "").trim() ? "border-rose-400" : "border-gray-300"}`} />
      </div>
    </div>
  );
}
